import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Cip,
  cn,
  FiltreKarti,
  Ikon,
  Iskelet,
  MetinAlani,
  MiniCubuk,
  Rozet,
  Tablo,
  type RozetTonu,
  Kart,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  haftaOzetleri,
  ogrenciDurumDegistir,
  ogrenciListesi,
  ogrenciMuvafakat,
  veliListesi,
  type OgrenciSatiri,
  type VeliOzeti,
} from "./api";
import { OgrenciYeni } from "./OgrenciYeni";
import { SayfaBasligiEki } from "./Kabuk";

/**
 * OGRENCILER — F-PPL
 *
 * ================================================================
 * DUGMELER SUNUCUDAN
 * ================================================================
 * Hangi durum gecisinin mumkun oldugu (`izinliGecisler`) ve hangisinin
 * gerekce istedigi (`gerekceIsteyenler`) SUNUCUDAN geliyor. Ekran
 * gecis tablosu tutsaydi `ogrenci-durum.ts` ile ayrildigi gun
 * tiklanabilir bir dugme gosterir, sunucu reddederdi.
 *
 * ================================================================
 * LISTEDE ILETISIM BILGISI YOK
 * ================================================================
 * E-posta yok. Telefon (ogrenci + birincil veli) yalniz sunucu yanitinda
 * VARSA gosterilir: sunucu `kisi.veli.goruntule` kapsami olmayana alani hic
 * gondermiyor (Emre karari 07.10.2026, sürüm 9a). Excel/CSV disa aktarma YOK.
 */

/**
 * Semadaki `StudentStatus` enum'unun TAMAMI.
 *
 * Ilk yazimda `PAUSED` ve `ARCHIVED` gibi UYDURMA anahtarlar vardi ve
 * ekranda ham "FROZEN" yaziyordu — canli denemede goruldu. Sekiz
 * degerin hepsi burada; eksik kalani `??` ile ham gostermek, bir
 * sonraki eksigi de gizlerdi.
 */
const DURUM_ADI: Record<string, string> = {
  PROSPECT: "Aday",
  ONBOARDING: "Kayıt sürüyor",
  ACTIVE: "Aktif",
  FROZEN: "Donduruldu",
  EXPIRED: "Paketi bitti",
  LEFT: "Ayrıldı",
  GRADUATED: "Mezun",
  ARCHIVED: "Arşiv",
};

/**
 * Durumun cip TONU.
 *
 * Once her duruma elle bir sinif dizisi yaziliydi ve tonlar `Cip`in
 * kendi tablosundan AYRISIYORDU (`bg-basarili-500/15` gibi — o birlesim
 * 04.09.2026'da olculdu, 2,60 ile AA'nin altinda). Tek bir ton adi
 * vermek, kontrast kararini bilesende TEK yerde tutuyor.
 */
const DURUM_TONU: Record<string, RozetTonu> = {
  PROSPECT: "notr",
  ONBOARDING: "bilgi",
  ACTIVE: "onay",
  FROZEN: "uyari",
  EXPIRED: "uyari",
  LEFT: "notr",
  // MEZUN bir SAYI degil KATEGORI (cizimin kendi notu): basari ya da
  // basarisizlik bildirmiyor, ogrencinin nerede oldugunu soyluyor.
  GRADUATED: "bilgi",
  ARCHIVED: "notr",
};

/** `GradeLevel` enum'unun tamami — G5..G12, GRADUATE, OTHER. */
const SINIF_ADI: Record<string, string> = {
  G5: "5. sınıf",
  G6: "6. sınıf",
  G7: "7. sınıf",
  G8: "8. sınıf",
  G9: "9. sınıf",
  G10: "10. sınıf",
  G11: "11. sınıf",
  G12: "12. sınıf",
  GRADUATE: "Mezun",
  OTHER: "Diğer",
};

export function Ogrenciler({
  onOgrenciSec,
  onProfilAc,
  ekleyebilir = false,
  analizGorebilir = false,
  muvafakatKaydedebilir = false,
  durumDegistirebilir = false,
  onVeliEkle,
}: {
  onOgrenciSec?: (id: string, ad: string) => void;
  /**
   * Ada dokununca Ogrenci 360° — UI/UX Dalga 1 (mutabakat K9/M-03/F06).
   * Kanitta koc listede ogrenciye tikliyor, hicbir sey olmuyordu; tek yol
   * "Programına git"ti. Verilmezse (360° bu kullaniciya kapali) ad duz
   * metin kalir — acilamayacak bir ekrana goturen baglanti yanlis vaat.
   */
  onProfilAc?: (id: string, ad: string) => void;
  /**
   * `kisi.ogrenci.olustur` izni var mı — `Uygulama.tsx` veriyor.
   *
   * İzni olmayana düğmeyi GÖSTERİP sunucuya reddettirmek de mümkündü;
   * gösterilmiyor. Basılamayacak bir düğme, kullanıcıya yapabileceği
   * bir işi vaat edip geri alıyor. Varsayılan `false`: bir çağrı yeri
   * bunu geçirmeyi unutursa düğme çıkmaz — yanlışlıkla açık kalmaz.
   */
  ekleyebilir?: boolean;
  /**
   * `analiz.ogrenci.goruntule` var mı — "Bu hafta" mini çubuğu (dalga 3).
   * Yoksa sütun hiç çizilmez ve İSTEK ATILMAZ (her ret denetim kaydına düşer).
   * Varsayılan `false`: çağıran unutursa sütun yok, istek yok.
   */
  analizGorebilir?: boolean;
  /**
   * `kvkk.muvafakat.kaydet` var mı — `Uygulama.tsx` veriyor (F-PPL-05b).
   * Sunucu bu ucu `kisi.ogrenci.duzenle`ye değil ayrı izne bağlıyor; izni olmayan
   * (ör. koç) düğmeye basınca 403 alırdı. Yoksa düğme çıkmaz, "muvafakat bekleniyor"
   * bilgisi salt okunur kalır. Varsayılan `false`: çağıran unutursa düğme açık kalmaz.
   */
  muvafakatKaydedebilir?: boolean;
  /**
   * `kisi.ogrenci.durum_degistir` var mı — `Uygulama.tsx` veriyor. Sunucu durum ucunu artık
   * `kisi.ogrenci.duzenle`ye değil bu ayrı izne bağlıyor (yalnız yönetim taşır; koç taşımaz). İzin yoksa
   * "Durum değiştir" bölümü HİÇ çizilmez: koça çalışmayacak bir düğme vaat etmeyiz. Varsayılan `false`:
   * çağıran unutursa düğme açık kalmaz.
   */
  durumDegistirebilir?: boolean;
  /**
   * Öğrenciye bağlı veli yoksa muvafakat formu yerine çıkan "Veli ekle" yönlendirmesi (öğrenci dosyasının
   * veli bölümüne götürür). Yalnız veli bağlama izni olan ve dosya ekranı açık kullanıcıya verilir; verilmezse
   * yalnız açıklama metni görünür.
   */
  onVeliEkle?: (id: string, ad: string) => void;
}) {
  const [arama, setArama] = useState("");
  const [acik, setAcik] = useState<string | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["ogrenciler"],
    queryFn: ogrenciListesi,
  });

  /*
    MUVAFAKAT SAYIMI SUZGECTEN ONCE: ozet BUTUN listeyi anlatiyor.
    Suzulmus liste uzerinden saysaydik, "Aktif" suzgeci acikken
    bekleyen sayisi duserdi ve uzman sorunun kucuduugunu sanirdi.

    IKI SAYI DA AYNI KUMEDEN: paydayi `data.toplam` (sunucudaki toplam)
    yapsaydik, bekleyen sayisi yuklenmis satirlardan gelirken payda
    butun kayitlardan gelirdi ve oran YANLIS olurdu. Ayni kumeden iki
    sayi, farkli kumelerden iki sayidan iyidir.
  */
  const tumu = data?.ogrenciler ?? [];
  const bekleyenSayisi = tumu.filter((o) => o.muvafakatBekliyor).length;
  // Sütun, sunucu alanı GÖNDERDİYSE var; ekran izin kodu bilmiyor ve kendi kararını vermiyor.
  const telefonGorunur = tumu.some((o) => o.telefon !== undefined || o.veliTelefon !== undefined);

  /*
    BU HAFTA MİNİ ÇUBUĞU — dalga 3. Satır başına istek DEĞİL: yüklenen
    listenin kimlikleri tek istekte (en çok 100 — liste ucunun sınırı).
    Sunucu kimlikleri KENDİ kapsamıyla kesiştiriyor; cevapta olmayan satır
    "ölçülmedi" gösterir, 0 değil.
  */
  const kimlikler = tumu.map((o) => o.id);
  const haftaOzeti = useQuery({
    queryKey: ["hafta-ozeti", kimlikler],
    queryFn: () => haftaOzetleri(kimlikler),
    enabled: analizGorebilir && kimlikler.length > 0,
    retry: false,
  });
  const ozetHaritasi = new Map(
    (haftaOzeti.data?.ogrenciler ?? []).map((s) => [s.ogrenciId, s] as const),
  );
  const yuzdeDegeri = (o: number | null | undefined) =>
    o === null || o === undefined ? null : Math.round(o * 1000) / 10;

  const suzulmus = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase("tr");
    if (q.length === 0) return data?.ogrenciler ?? [];
    return (data?.ogrenciler ?? []).filter((o) =>
      `${o.ad} ${o.soyad} ${o.studentNo}`.toLocaleLowerCase("tr").includes(q),
    );
  }, [data, arama]);

  // Form, listenin durumundan BAGIMSIZ: liste alinamadiginda da kayit
  // acilabilmeli. Aksi halde tek bir okuma hatasi, sisteme veri
  // girmeyi de durdururdu.
  if (yeniAcik) return <OgrenciYeni onKapat={() => setYeniAcik(false)} />;

  if (isPending) return <Iskelet satir={4} />;

  if (isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Öğrenci listesi alınamadı"
        aciklama={error instanceof ApiHatasi ? error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  if (data.ogrenciler.length === 0) {
    /*
      "Kapsaminizda" deniyor, "kayit yok" DEGIL: uzman yalnizca kendi
      ogrencilerini gorur ve bos liste, sistemde hic ogrenci olmadigi
      anlamina gelmez.
    */
    return (
      <BosDurum
        baslik="Kapsamınızda öğrenci yok"
        aciklama="Yalnızca size atanmış öğrenciler listelenir; bu, sistemde kayıt olmadığı anlamına gelmez."
        /*
          Bos durum ARTIK CIKMAZ DEGIL. Once burada yalnizca bir cumle
          vardi: sistem sifirdan aciliyor, ilk gun herkes bu ekrani
          goruyor ve gorecegi tek sey "ogrenci yok" oluyordu — ne
          yapacagi yazmiyordu.
        */
        eylem={
          ekleyebilir ? <Buton onClick={() => setYeniAcik(true)}>Yeni öğrenci</Buton> : undefined
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama="Öğrenci bilgilerini yönetin, filtreleyin ve profillerine geçin." />
      {/*
        UNİKOÇ "Öğrenci İşleri" filtre kartı (tarama-2 §3.7): arama + Uygula/Temizle. Arama
        anında süzdüğü için "Uygula" düğmesi yok (çalışmayan düğme vaat edilmez); "Yeni öğrenci"
        UNİKOÇ'ta yok ama bizde ilk gün tek giriş yolu — kartın sağ altında birincil düğme.
      */}
      <FiltreKarti
        baslik="Öğrenci ve Filtreler"
        eylemler={
          <>
            <Buton gorunum="kesikli" onClick={() => setArama("")} disabled={arama === ""}>
              Temizle
            </Buton>
            {ekleyebilir && <Buton onClick={() => setYeniAcik(true)}>Yeni öğrenci</Buton>}
            <span className="text-metin-ikincil ml-auto text-xs">
              {suzulmus.length} / {data.toplam}
            </span>
          </>
        }
      >
        <Alan
          etiket="Ara"
          value={arama}
          onChange={(e) => setArama(e.target.value)}
          placeholder="Ad veya öğrenci no ile ara"
        />
      </FiltreKarti>

      {suzulmus.length === 0 ? (
        <p className="text-notr-600 text-sm">Aramaya uyan öğrenci yok.</p>
      ) : (
        /**
         * ================================================================
         * KART DEĞİL TABLO — ÖLÇÜLDÜ
         * ================================================================
         * Kart satırı 126px yer kaplıyordu ve 1280×720'de 21 öğrencinin
         * yalnızca 5'i görünüyordu. Koç sabah bu listeye TARAMA yapmak
         * için bakıyor: kim hangi sınıfta, kimin durumu ne. Kartta her
         * alan kendi satırında ve göz her kartta baştan hizalanmak
         * zorunda kalıyordu.
         *
         * `Tablo` dar ekranda karta düşüyor — proje mobil öncelikli ve
         * yatay kayan tablo telefonda okunmuyor.
         */
        <>
          {/*
          ================================================================
          MUVAFAKAT OZETI — "21'in 19'u, bu TEK BAKISTA gorunmeli"
          ================================================================
          Cizimin kendi notu. Satir bazinda isaret zaten vardi ama
          sayfa basinda ozet YOKTU: yirmi bir satirlik bir listede iki
          bekleyeni gormek icin butun listeyi taramak gerekiyordu.

          Muvafakat bekleyen ogrenci sifresi belirlenmis olsa bile
          GIRIS YAPAMIYOR (F-PPL-05b); kac tane oldugunu bilmeyen uzman
          "neden giremiyor" cagrilarini tek tek karsiliyor.

          Bant `uyari` ailesinde ve bilgi bandi kalibinda: `uyari-50`
          dolgu, `uyari-200` kenar, koyu metin. Sifir bekleyen varken
          CIZILMIYOR — bos bir uyari bandi, bakilmayan bir uyari
          uretir.
        */}
          {bekleyenSayisi > 0 && (
            <div className="rounded-kart border-uyari-200 bg-uyari-50 flex items-center gap-3 border px-[18px] py-[15px]">
              <Ikon ad="uyari" boyut="normal" className="text-uyari-800 shrink-0" />
              <div className="flex min-w-0 flex-col gap-0.5">
                <p className="text-uyari-800 text-sm font-medium">
                  {tumu.length} öğrencinin {tumu.length - bekleyenSayisi} tanesinde veli muvafakati
                  var
                </p>
                <p className="text-uyari-800 text-[13px]">
                  {bekleyenSayisi} öğrenci muvafakat beklediği için giriş yapamıyor.
                </p>
              </div>
            </div>
          )}

          <Kart gorunum="unk" olarak="div" className="p-2 md:p-4">
            <Tablo
              baslikTuru="degrade"
              satirlar={suzulmus}
              anahtar={(o) => o.id}
              kartBasligi={0}
              sutunlar={[
                {
                  baslik: "Ad",
                  mobil: true,
                  /*
                  Satirin tamami degil ADIN KENDISI dugme: `<tr onClick>`
                  klavyeyle ulasilmiyor ve ekran okuyucuda duyurulmuyor
                  (gerekce `Tablo.tsx` basliginda).
                */
                  hucre: (o) => (
                    <span className="flex items-center gap-2.5">
                      {/* Fotoğraf verisi listede yok; baş harfler (UNİKOÇ yuvarlak avatar yerine). */}
                      <span
                        aria-hidden="true"
                        className="bg-mor-50 text-mor-700 grid size-8 shrink-0 place-items-center rounded-full text-xs font-bold"
                      >
                        {`${o.ad[0] ?? ""}${o.soyad[0] ?? ""}`.toLocaleUpperCase("tr-TR")}
                      </span>
                      {onProfilAc ? (
                        <button
                          type="button"
                          onClick={() => onProfilAc(o.id, `${o.ad} ${o.soyad}`)}
                          className={cn(
                            "text-marka-700 rounded-alan text-left font-medium hover:underline",
                            "focus-visible:outline-marka-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                          )}
                        >
                          {o.ad} {o.soyad}
                          {/* Boşluk span'in DIŞINDA: içeride erişilebilir ad hesabı onu yutuyor
                          ("Ayşe Yılmaz— Öğrenci 360°" ölçüldü, 05.10.2026). */}{" "}
                          <span className="sr-only">— Öğrenci 360°</span>
                        </button>
                      ) : (
                        `${o.ad} ${o.soyad}`
                      )}
                    </span>
                  ),
                  className: "font-medium",
                },
                { baslik: "No", sayisal: true, hucre: (o) => o.studentNo },
                ...(telefonGorunur
                  ? [
                      {
                        baslik: "Telefon",
                        hucre: (o: OgrenciSatiri) => telefonMetni(o.telefon),
                      },
                      {
                        baslik: "Veli telefonu",
                        hucre: (o: OgrenciSatiri) => telefonMetni(o.veliTelefon),
                      },
                    ]
                  : []),
                ...(analizGorebilir
                  ? [
                      {
                        baslik: "Bu hafta",
                        mobil: true,
                        /*
                        Yön bir BAŞARI HÜKMÜ değil: ok ve metinle "artış/azalış/
                        sabit" söylüyor, renkle değil. Önceki hafta ölçülmediyse
                        "Yön ölçülmedi" (sabit sayılmıyor).
                      */
                        hucre: (o: OgrenciSatiri) => {
                          if (haftaOzeti.isPending) return <span className="text-notr-600">…</span>;
                          if (haftaOzeti.isError) return <span className="text-notr-600">—</span>;
                          const s = ozetHaritasi.get(o.id);
                          return (
                            <MiniCubuk
                              baslik={`${o.ad} ${o.soyad} — bu hafta tamamlama`}
                              deger={yuzdeDegeri(s?.buHafta.tamamlamaOrani)}
                              oncekiDeger={yuzdeDegeri(s?.oncekiHafta.tamamlamaOrani)}
                              bosMesaji="Bu hafta yayınlanmış görev yok"
                              yalin
                            />
                          );
                        },
                      },
                    ]
                  : []),
                {
                  baslik: "Sınıf",
                  mobil: true,
                  hucre: (o) => SINIF_ADI[o.sinif] ?? o.sinif,
                },
                {
                  baslik: "Durum",
                  mobil: true,
                  /*
                CIP `radius-alan` — `Ogrenciler.dc.html`den olculdu
                (2px 9px, 12px/500). Tam yuvarlak DEGIL: `radius-rozet`
                sayi tasiyan rozet icin, bu ise siniflandirma etiketi.
              */
                  hucre: (o) => (
                    <Cip ton={DURUM_TONU[o.status] ?? "notr"}>
                      {DURUM_ADI[o.status] ?? o.status}
                    </Cip>
                  ),
                },
                {
                  baslik: "Uyarı",
                  mobil: true,
                  hucre: (o) =>
                    /*
                  MUVAFAKAT BEKLEYEN açıkça işaretli: o öğrencinin hesabı
                  şifresi belirlenmiş olsa bile giriş YAPAMIYOR
                  (F-PPL-05b) ve bunu bilmeden "neden giremiyor" diye
                  aranır.
                */
                    o.muvafakatBekliyor || o.uzmanIzinde ? (
                      <span className="flex flex-wrap gap-1">
                        {o.muvafakatBekliyor && <Rozet ton="uyari">Veli muvafakati bekliyor</Rozet>}
                        {/*
                        İZİNLİ UZMAN İŞARETİ — F-PPL-11b: "izinli uzmanın
                        öğrencileri koordinatör ekranında 'izinli uzman'
                        işaretiyle listelenir."

                        Ton `uyari` DEĞİL `notr`: izinli bir uzman bir
                        ARIZA değil, bilinmesi gereken bir durum. Uyarı
                        tonu kullansaydık gerçek uyarıları (muvafakat
                        eksikliği gibi) değersizleştirirdi — bu ekranda
                        ikisi yan yana duruyor ve farkları görünmeli.
                      */}
                        {o.uzmanIzinde && <Rozet ton="notr">Uzmanı izinde</Rozet>}
                      </span>
                    ) : null,
                },
                {
                  baslik: "İşlem",
                  // Kartta "İşlem:" etiketi yazilmasin (UI/UX Dalga 1, M-03).
                  etiketGizli: true,
                  // Telefonda da gorunur: isaretlenmeseydi `Tablo` bu
                  // sutunu dar ekranda gizler ve satirin TEK islemi
                  // telefondan erisilemez olurdu (07.08.2026, 375px).
                  mobil: true,
                  /*
                  Yapilacak islem yoksa NULL — bos div degil. Telefonda
                  hucre kendi etiketini yaziyor ve bos bir div altinda
                  yalniz "İşlem:" kaliyordu (UI/UX Dalga 1, M-03);
                  `Tablo` bos hucreyi dar ekranda gizliyor.
                */
                  hucre: (o) =>
                    !onOgrenciSec && !islemVarMi(o, durumDegistirebilir) ? null : (
                      <div className="flex flex-wrap justify-end gap-1">
                        {onOgrenciSec && (
                          <Buton
                            gorunum="sessiz"
                            onClick={() => onOgrenciSec(o.id, `${o.ad} ${o.soyad}`)}
                          >
                            {/*
                        "Programı" diye kısaltmıştım; tabloda "İşlem"
                        başlığının altında tek başına belirsiz okunuyor
                        ("programı ne?"). Sütun genişliği 1440px'te
                        gerçek bir kısıt değil.
                      */}
                            Programına git
                          </Buton>
                        )}
                        {/*
                    İşlem düğmesi yalnızca YAPILABİLECEK bir şey varsa
                    çıkıyor: arşivlenmiş bir öğrencide boş bir panel
                    açmak, kullanıcıya "bir şey unuttum" hissi verirdi.
                  */}
                        {islemVarMi(o, durumDegistirebilir) && (
                          <Buton
                            gorunum="sessiz"
                            onClick={() => setAcik(acik === o.id ? null : o.id)}
                          >
                            {acik === o.id ? "Kapat" : "İşlemler"}
                          </Buton>
                        )}
                      </div>
                    ),
                },
              ]}
              altSatir={(o) =>
                acik === o.id ? (
                  <div className="border-notr-100 bg-notr-50 border-b px-3 py-3">
                    <Islemler
                      ogrenci={o}
                      muvafakatKaydedebilir={muvafakatKaydedebilir}
                      durumDegistirebilir={durumDegistirebilir}
                      onVeliEkle={onVeliEkle}
                    />
                  </div>
                ) : null
              }
            />
          </Kart>
        </>
      )}
    </div>
  );
}

/** Satırda "İşlemler" açacak bir şey var mı: izinli geçiş (izin varsa) ya da bekleyen muvafakat bilgisi. */
/** `null` = numara kayıtlı değil: boş hücre yerine "—" (yetkisizlikle karışmasın). */
function telefonMetni(t: string | null | undefined): string {
  return t ? t : "—";
}

function islemVarMi(o: OgrenciSatiri, durumDegistirebilir: boolean): boolean {
  return (durumDegistirebilir && o.izinliGecisler.length > 0) || o.muvafakatBekliyor;
}

function Islemler({
  ogrenci,
  muvafakatKaydedebilir,
  durumDegistirebilir,
  onVeliEkle,
}: {
  ogrenci: OgrenciSatiri;
  muvafakatKaydedebilir: boolean;
  durumDegistirebilir: boolean;
  onVeliEkle?: (id: string, ad: string) => void;
}) {
  const qc = useQueryClient();
  const [hedef, setHedef] = useState<string>("");
  const [gerekce, setGerekce] = useState("");
  const [muvafakatGerekce, setMuvafakatGerekce] = useState("");
  const [seciliBagId, setSeciliBagId] = useState("");

  /*
    F-PPL-05b — muvafakat bir VELİNİN beyanı: kimden alındığı seçilir. Liste yalnız form görünürken istenir
    (yetkisiz kullanıcıya boşuna istek ve denetim kaydı atılmaz). Anahtar öğrenci dosyasındaki veli listesiyle
    ORTAK ("veliler"): biri değişirse öbürü de taze.
  */
  const muvafakatFormuAcik = ogrenci.muvafakatBekliyor && muvafakatKaydedebilir;
  const veliler = useQuery({
    queryKey: ["veliler", ogrenci.id],
    queryFn: () => veliListesi(ogrenci.id),
    enabled: muvafakatFormuAcik,
    retry: false,
  });
  // Seçim listede hâlâ var mı: sunucu "bağ geçersiz" derse liste yenilenir, eski seçim sessizce düşer.
  const seciliVeli = veliler.data?.find((v) => v.id === seciliBagId);

  const yenile = () => void qc.invalidateQueries({ queryKey: ["ogrenciler"] });

  const durum = useMutation({
    mutationFn: () =>
      ogrenciDurumDegistir(ogrenci.id, {
        durum: hedef,
        gerekce: gerekce.trim() || undefined,
      }),
    onSuccess: () => {
      setHedef("");
      setGerekce("");
      yenile();
    },
  });

  const muvafakat = useMutation({
    mutationFn: (g: { bagId: string; veliAdi: string }) =>
      ogrenciMuvafakat(ogrenci.id, { bagId: g.bagId, gerekce: muvafakatGerekce.trim() }),
    onSuccess: () => {
      setMuvafakatGerekce("");
      setSeciliBagId("");
      yenile();
    },
    // Bağ geçersiz/değişmiş olabilir (başka biri kaldırdı): listeyi tazele, seçim yeniden yapılsın.
    onError: () => void qc.invalidateQueries({ queryKey: ["veliler", ogrenci.id] }),
  });

  // Gerekce zorunlulugu SUNUCUDAN gelen listeden okunuyor.
  const gerekceZorunlu = hedef !== "" && ogrenci.gerekceIsteyenler.includes(hedef);

  return (
    <div className="border-notr-200 mt-3 flex flex-col gap-4 border-t pt-3">
      {ogrenci.muvafakatBekliyor && !muvafakatKaydedebilir && (
        <div className="flex flex-col gap-1">
          <p className="text-notr-900 text-sm font-medium">Veli muvafakati</p>
          <p className="text-notr-600 text-xs">
            Veli muvafakati bekleniyor. Kaydetme yetkisi olan bir yönetici ya da satış yetkilisi
            bunu kaydedebilir.
          </p>
        </div>
      )}

      {muvafakatFormuAcik && (
        <div className="flex flex-col gap-2">
          <p className="text-notr-900 text-sm font-medium">Veli muvafakati</p>
          {veliler.isPending ? (
            <Iskelet satir={2} />
          ) : veliler.isError ? (
            <div className="flex flex-col gap-2">
              <Hata hata={veliler.error} />
              <div>
                <Buton gorunum="ikincil" onClick={() => void veliler.refetch()}>
                  Tekrar dene
                </Buton>
              </div>
            </div>
          ) : veliler.data.length === 0 ? (
            /*
              Bağlı veli yoksa muvafakat KİMDEN alınmış diye kaydedilemez (sunucu `BAGLI_VELI_YOK` der);
              kullanıcıyı çıkmaza sokmak yerine ne yapacağı söyleniyor.
            */
            <div className="flex flex-col gap-2">
              <p className="text-notr-700 text-sm">
                Muvafakat için önce öğrenciye bağlı bir veli kaydı eklenmeli.
              </p>
              {onVeliEkle && (
                <div>
                  <Buton
                    gorunum="ikincil"
                    onClick={() => onVeliEkle(ogrenci.id, `${ogrenci.ad} ${ogrenci.soyad}`)}
                  >
                    Veli ekle
                  </Buton>
                </div>
              )}
            </div>
          ) : (
            <>
              <p className="text-notr-600 text-xs">
                Muvafakat kaydedilince, şifresini zaten belirlemiş öğrencinin hesabı aktifleşir.
                Muvafakatı veren veliyi seçin; gerekçe zorunlu: muvafakatin nasıl alındığı altı ay
                sonra sorulabilir olmalı.
              </p>
              <VeliSecimi
                veliler={veliler.data}
                secili={seciliBagId}
                onSec={setSeciliBagId}
                disabled={muvafakat.isPending}
              />
              <MetinAlani
                etiket="Muvafakat gerekçesi"
                rows={2}
                value={muvafakatGerekce}
                onChange={(e) => setMuvafakatGerekce(e.target.value)}
                placeholder="Muvafakat nasıl alındı?"
              />
              <div>
                <Buton
                  onClick={() => {
                    if (seciliVeli)
                      muvafakat.mutate({
                        bagId: seciliVeli.id,
                        veliAdi: `${seciliVeli.firstName} ${seciliVeli.lastName}`,
                      });
                  }}
                  disabled={
                    muvafakat.isPending || !seciliVeli || muvafakatGerekce.trim().length < 3
                  }
                >
                  {muvafakat.isPending ? "Kaydediliyor…" : "Muvafakati kaydet"}
                </Buton>
              </div>
            </>
          )}
          {muvafakat.isError && <Hata hata={muvafakat.error} />}
        </div>
      )}

      {/* Başarı bildirimi formun DIŞINDA: liste tazelenince bekleyen işareti kalkar, form kapanır; mesaj kalmalı. */}
      {muvafakat.isSuccess && (
        <p role="status" className="text-onay-700 text-sm">
          Muvafakat kaydedildi. Muvafakatı veren: {muvafakat.variables.veliAdi}.
          {muvafakat.data?.aktiflesti ? " Öğrencinin hesabı aktifleşti." : ""}
        </p>
      )}

      {durumDegistirebilir && ogrenci.izinliGecisler.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-notr-900 text-sm font-medium">Durum değiştir</p>
          <div className="flex flex-wrap gap-1.5">
            {ogrenci.izinliGecisler.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setHedef(hedef === d ? "" : d)}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs",
                  hedef === d
                    ? "border-marka-600 bg-marka-50 text-marka-700"
                    : "border-notr-300 bg-notr-0 text-notr-700",
                )}
              >
                {DURUM_ADI[d] ?? d}
              </button>
            ))}
          </div>

          {hedef !== "" && (
            <>
              {gerekceZorunlu && (
                <MetinAlani
                  etiket="Gerekçe (zorunlu)"
                  rows={2}
                  value={gerekce}
                  onChange={(e) => setGerekce(e.target.value)}
                />
              )}
              <div>
                <Buton
                  onClick={() => durum.mutate()}
                  disabled={durum.isPending || (gerekceZorunlu && gerekce.trim().length < 3)}
                >
                  {durum.isPending ? "Değiştiriliyor…" : `"${DURUM_ADI[hedef] ?? hedef}" yap`}
                </Buton>
              </div>
            </>
          )}
          {durum.isError && <Hata hata={durum.error} />}
        </div>
      )}
    </div>
  );
}

/**
 * Muvafakatı veren velinin seçimi. VARSAYILAN SEÇİLİ YOK: tek veli bile olsa seçimi kullanıcı yapar
 * ("Önerilen" yalnız ipucu) — muvafakatın kimden alındığı bir hukuki kayıt, varsayılanla doldurulmaz.
 */
function VeliSecimi({
  veliler,
  secili,
  onSec,
  disabled,
}: {
  veliler: readonly VeliOzeti[];
  secili: string;
  onSec: (bagId: string) => void;
  disabled: boolean;
}) {
  return (
    <fieldset className="flex flex-col gap-1.5" disabled={disabled}>
      <legend className="text-notr-900 mb-1 text-sm font-medium">Muvafakatı veren veli</legend>
      {veliler.map((v) => (
        <label
          key={v.id}
          className={cn(
            "rounded-alan flex min-h-11 cursor-pointer items-center gap-2 border px-3 py-2 text-sm",
            secili === v.id ? "border-marka-600 bg-marka-50" : "border-notr-300 bg-notr-0",
          )}
        >
          <input
            type="radio"
            name="muvafakat-veli"
            value={v.id}
            checked={secili === v.id}
            onChange={() => onSec(v.id)}
          />
          <span className="text-notr-900">
            {v.firstName} {v.lastName}
          </span>
          <span className="text-notr-600">({v.relation})</span>
          {veliler.length === 1 && <Rozet ton="bilgi">Önerilen</Rozet>}
        </label>
      ))}
    </fieldset>
  );
}

/** Sunucunun gerekçesi olduğu gibi gösteriliyor. */
function Hata({ hata }: { hata: unknown }) {
  return (
    <p className="text-hata-500 text-xs">
      {hata instanceof ApiHatasi ? hata.message : "İşlem tamamlanamadı."}
    </p>
  );
}
